/**
 * Event date utilities for display purposes
 * Clean, focused functions for extracting and formatting event dates
 */

import { differenceInCalendarDays } from 'date-fns';
import { isEventEntity } from './entityTypeDetector.ts';

// ============================================================================
// Types
// ============================================================================

export interface EventDates {
  start: Date | null;
  end: Date | null;
  allDay: boolean;
}

export interface CalendarBlockParts {
  month: string;
  day: string;
  weekday: string;
}

// ============================================================================
// Constants
// ============================================================================

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const;
const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;

// ============================================================================
// Date Extraction
// ============================================================================

/**
 * Parse a date string to a Date object
 * Handles various formats: ISO, date-only, MM-DD
 */
function parseDate(value: string | undefined | null, allDay = false): Date | null {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  // Date-only format: YYYY-MM-DD - parse as local date (noon to avoid timezone issues)
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return new Date(`${trimmed}T12:00:00`);
  }

  // Only explicit all-day values represent civil dates at UTC midnight.
  if (allDay && /^\d{4}-\d{2}-\d{2}T00:00:00(\.000)?Z?$/.test(trimmed)) {
    const datePart = trimmed.slice(0, 10);
    return new Date(`${datePart}T12:00:00`);
  }

  // Standard ISO - parse directly
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  // MM-DD format (no year) - assume current/next year
  const monthDayMatch = trimmed.match(/^-?(\d{2})-(\d{2})$/);
  if (monthDayMatch) {
    const month = Number(monthDayMatch[1]);
    const day = Number(monthDayMatch[2]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const now = new Date();
      const year = now.getFullYear();
      let date = new Date(year, month - 1, day);
      if (date.getTime() < now.getTime()) {
        date = new Date(year + 1, month - 1, day);
      }
      return date;
    }
  }

  return null;
}

/**
 * Safely get a string value from a nested object path
 */
function getString(obj: unknown, ...paths: string[]): string | undefined {
  for (const path of paths) {
    const keys = path.split('.');
    let value: unknown = obj;
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = (value as Record<string, unknown>)[key];
      } else {
        value = undefined;
        break;
      }
    }
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
}

/** Device civil key, used for the person's selected calendar day. */
export function calendarDayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

const dateOnly = (value?: string) => Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value));
const hasOffset = (value: string) => /[T ]/.test(value) && /(?:Z|[+-]\d{2}(?::?\d{2})?)$/i.test(value);

/** The API's documented fallback is UTC, never the viewing device's zone. */
function venueClock(instant: Date, zone?: string): string {
  const format = (timeZone: string) => new Intl.DateTimeFormat('en-US', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(instant);
  let parts: Intl.DateTimeFormatPart[];
  try { parts = format(zone ?? 'UTC'); } catch { parts = format('UTC'); }
  const part = (type: string) => parts.find(value => value.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}:${part('second')}.${String(instant.getMilliseconds()).padStart(3, '0')}`;
}

function venueDate(instant: Date, zone?: string): Date {
  return civilDate(venueClock(instant, zone))!;
}

/** Numeric ordering of civil clocks, independent of the device's DST rules. */
function wallClock(value: string): number | null {
  const raw = value.replace(/(?:Z|[+-]\d{2}(?::?\d{2})?)$/i, '');
  const parsed = new Date(`${raw}Z`);
  return Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== raw.slice(0, 10) ? null : parsed.getTime();
}

function eventStrings(entity: unknown) {
  const e = entity && typeof entity === 'object' ? entity as Record<string, unknown> : {};
  const calendar = e.calendar_data as Record<string, unknown> | undefined;
  return {
    startLocal: validLocal(getString(e, 'calendar_data.dtstart_local')),
    endLocal: validLocal(getString(e, 'calendar_data.dtend_local')),
    startUtc: getString(e, 'calendar_data.dtstart_utc', 'dtstart_utc'),
    endUtc: getString(e, 'calendar_data.dtend_utc', 'dtend_utc'),
    startOther: getString(e, 'calendar_data.dtstart', 'dtstart', 'dstart', 'start_date', 'startDate', 'date'),
    endOther: getString(e, 'calendar_data.dtend', 'dtend', 'dend', 'end_date', 'endDate'),
    zone: getString(e, 'calendar_data.tzid', 'calendar_data.timezone', 'calendar_data.time_zone'),
    allDay: calendar?.all_day === true,
  };
}

function validVenueZone(zone?: string): boolean {
  if (!zone) return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: zone }); return true; } catch { return false; }
}

/** Offset-bearing local values are instants when the venue zone is known.
 * Otherwise their declared civil clock remains the display authority. */
function localDisplayClock(value: string, zone?: string): string {
  const instant = hasOffset(value) ? instantDate(value, false) : null;
  return instant && validVenueZone(zone) ? venueClock(instant, zone) : value;
}

function currentDisplayClock(now: Date, zone?: string, local?: string): string {
  if (validVenueZone(zone) || !local || !hasOffset(local)) return venueClock(now, zone);
  const offset = local.match(/([+-])(\d{2})(?::?(\d{2}))?$/);
  const minutes = offset ? (Number(offset[2]) * 60 + Number(offset[3] ?? 0)) * (offset[1] === '-' ? -1 : 1) : 0;
  return new Date(now.getTime() + minutes * 60_000).toISOString().slice(0, -1);
}

function validLocal(value?: string): string | undefined {
  const isoLocal = /^(?!0000)\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?(?:Z|[+-](?:[01]\d|2[0-3])(?::?[0-5]\d)?)?)?$/;
  return value && isoLocal.test(value) && civilDate(value) ? value : undefined;
}

function instantDate(value: string, explicitUtc: boolean): Date | null {
  const parsed = new Date(explicitUtc && !hasOffset(value) ? `${value}Z` : value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function civilDate(value: string): Date | null {
  const civil = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(civil)) return parseDate(value);
  const result = new Date(`${civil}T${value.length > 10 ? value.slice(11).replace(/(?:Z|[+-]\d{2}(?::?\d{2})?)$/i, '') : '12:00:00'}`);
  return Number.isNaN(result.getTime()) || calendarDayKey(result) !== civil ? null : result;
}

/** Scheduling/sorting contract: preserve Date epochs rather than venue display
 * containers. UTC-only inputs remain the actual stored instants. */
export function extractEventDates(entity: unknown): EventDates {
  const data = eventStrings(entity);
  return { start: parseDate(data.startLocal ?? data.startUtc ?? data.startOther, data.allDay),
    end: parseDate(data.endLocal ?? data.endUtc ?? data.endOther, data.allDay), allDay: data.allDay };
}

/** Display uses venue civil fields; these Date values are not interval instants. */
export function getEventDisplayDates(entity: unknown): EventDates {
  const data = eventStrings(entity);
  const display = (local?: string, utc?: string, other?: string): Date | null => {
    const raw = local ?? utc ?? other;
    if (!raw) return null;
    if (data.allDay || dateOnly(raw)) return civilDate(raw.slice(0, 10));
    if (local) return civilDate(localDisplayClock(raw, data.zone));
    const instant = instantDate(raw, Boolean(utc) || !hasOffset(raw));
    return instant ? venueDate(instant, data.zone) : null;
  };
  return { start: display(data.startLocal, data.startUtc, data.startOther),
    end: display(data.endLocal, data.endUtc, data.endOther), allDay: data.allDay || dateOnly(data.startLocal ?? data.startUtc ?? data.startOther) };
}

/** Calendar cells and relative labels share the same venue-day interpretation.
 * A timed end is exclusive; explicit all-day/date-only ends include the end day. */
export function eventCalendarDays(entity: unknown): { start: string | null; end: string | null } {
  const data = eventStrings(entity);
  const display = getEventDisplayDates(entity);
  let end = display.end;
  const endRaw = data.endLocal ?? data.endUtc ?? data.endOther;
  if (end && endRaw && !data.allDay && !dateOnly(endRaw)) {
    if (data.endLocal && hasOffset(data.endLocal)) {
      const instant = instantDate(data.endLocal, false);
      if (instant) end = civilDate(currentDisplayClock(new Date(instant.getTime() - 1), data.zone, data.endLocal));
    } else if (data.endLocal) {
      end = new Date(end.getTime() - 1);
    } else {
      const instant = instantDate(data.endUtc ?? endRaw, Boolean(data.endUtc) || !hasOffset(endRaw));
      if (instant) end = venueDate(new Date(instant.getTime() - 1), data.zone);
    }
  }
  const first = display.start ? calendarDayKey(display.start) : null;
  const last = end ? calendarDayKey(end) : null;
  return { start: first, end: first && last && last < first ? first : last };
}

export function eventOccursOnDay(entity: unknown, day: string): boolean {
  const { start, end } = eventCalendarDays(entity);
  return Boolean(start && start <= day && (end ?? start) >= day);
}

/**
 * Check if an entity is a past event (end/start date before today).
 * Returns false for non-events or events without parseable dates.
 * Pass a pre-computed `today` to avoid repeated Date allocation in loops.
 */
export function isPastEvent(entity: unknown, today?: Date): boolean {
  if (!isEventEntity(entity as any)) return false;
  const { start, end } = extractEventDates(entity);
  const cutoff = end || start;
  if (!cutoff) return false;
  if (!today) {
    today = new Date();
    today.setHours(0, 0, 0, 0);
  }
  return cutoff < today;
}

// ============================================================================
// Calendar Block Display
// ============================================================================

/**
 * Get calendar block display parts from a date
 * Returns { month: "MAR", day: "20", weekday: "FRI" }
 */
export function getCalendarBlockParts(date: Date): CalendarBlockParts {
  return {
    month: MONTHS[date.getMonth()],
    day: String(date.getDate()),
    weekday: WEEKDAYS[date.getDay()],
  };
}

/**
 * Check if two dates are the same calendar day
 */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// ============================================================================
// Time Formatting
// ============================================================================

/**
 * Format a time like "11 AM" or "11:30 AM"
 */
export function formatTime(date: Date, omitMinutesWhenZero = true): string {
  return formatClock(date.getHours(), date.getMinutes(), omitMinutesWhenZero);
}

function formatClock(hours: number, minutes: number, omitMinutesWhenZero = true): string {
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;

  if (omitMinutesWhenZero && minutes === 0) {
    return `${displayHours} ${period}`;
  }

  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
}

/**
 * Format a time range like "11 AM - 7 PM"
 * Returns null if times are identical or invalid
 */
export function formatTimeRange(start: Date, end: Date | null): string | null {
  const startTime = formatTime(start);

  if (!end) return startTime;

  const endTime = formatTime(end);
  if (startTime === endTime) return startTime;

  return `${startTime} - ${endTime}`;
}

function eventTimeLabel(zone: string | undefined, local?: string, utc?: string, other?: string): string | null {
  let raw = local ?? utc ?? other;
  if (!raw || dateOnly(raw)) return null;
  if (local) raw = localDisplayClock(local, zone);
  else {
    const instant = instantDate(raw, Boolean(utc) || !hasOffset(raw));
    if (!instant) return null;
    raw = venueClock(instant, zone);
  }
  const match = raw.match(/[T ](\d{2}):(\d{2})/);
  return match ? formatClock(Number(match[1]), Number(match[2])) : null;
}

/** Venue clock formatting never interprets a local-only value in device time. */
export function formatEventTimeRange(entity: unknown): string | null {
  const data = eventStrings(entity);
  if (data.allDay) return null;
  const start = eventTimeLabel(data.zone, data.startLocal, data.startUtc, data.startOther);
  const end = eventTimeLabel(data.zone, data.endLocal, data.endUtc, data.endOther);
  return start && end && start !== end ? `${start} - ${end}` : start;
}

// ============================================================================
// Relative Date Display
// ============================================================================

/**
 * Get a relative date string like "Today", "Tomorrow", "In 3 days"
 */
export function getRelativeDate(date: Date, now = new Date()): string {
  const days = differenceInCalendarDays(date, now);

  if (days < 0) {
    const absDays = Math.abs(days);
    if (absDays === 1) return 'Yesterday';
    return `${absDays} days ago`;
  }

  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days <= 6) return `In ${days} days`;

  const weeks = Math.floor(days / 7);
  if (weeks === 1) return 'In 1 week';
  if (weeks < 5) return `In ${weeks} weeks`;

  const months = Math.floor(days / 30);
  if (months === 1) return 'In 1 month';
  return `In ${months} months`;
}

/**
 * Get a combined relative date + time string
 * e.g., "Today @ 5 PM", "Tomorrow @ 11 AM"
 */
export function getRelativeDateWithTime(date: Date, allDay: boolean, now = new Date()): string {
  const relative = getRelativeDate(date, now);

  if (allDay) return relative;

  // Check if there's a meaningful time (not midnight)
  const hours = date.getHours();
  const minutes = date.getMinutes();
  if (hours === 0 && minutes === 0) return relative;

  return `${relative} @ ${formatTime(date)}`;
}


/** Relative status compares actual UTC instants when supplied. Local-only timed
 * metadata compares wall clocks in the venue zone, not in the viewing zone. */
export function getEventRelativeDate(entity: unknown, includeTime = true, now = new Date()): string | null {
  const data = eventStrings(entity);
  const dates = getEventDisplayDates(entity);
  if (!dates.start) return null;
  const nowClock = currentDisplayClock(now, data.zone, data.startLocal);
  const venueNow = civilDate(nowClock)!;
  const compare = (local?: string, utc?: string, other?: string): number | null => {
    const raw = local ?? utc ?? other;
    if (!raw) return null;
    if (data.allDay || dateOnly(raw)) {
      const civil = civilDate(raw.slice(0, 10));
      return civil ? differenceInCalendarDays(civil, venueNow) : null;
    }
    // Timed local display metadata never overrides a supplied UTC instant;
    // local date-only precision remains an inclusive civil-day interval.
    if (utc || !local || hasOffset(local)) {
      const instant = instantDate(utc ?? raw, Boolean(utc) || !hasOffset(raw));
      return instant ? instant.getTime() - now.getTime() : null;
    }
    const wall = wallClock(local ?? raw);
    const current = wallClock(nowClock);
    return wall !== null && current !== null ? wall - current : null;
  };
  const started = compare(data.startLocal, data.startUtc, data.startOther);
  const ended = compare(data.endLocal, data.endUtc, data.endOther);
  const endRaw = data.endLocal ?? data.endUtc ?? data.endOther;
  const inclusiveEnd = data.allDay || dateOnly(endRaw);
  if (started !== null && ended !== null && started <= 0 && (inclusiveEnd ? ended >= 0 : ended > 0)) return 'Ongoing';
  const relative = getRelativeDate(dates.start, venueNow);
  const time = includeTime && !dates.allDay ? eventTimeLabel(data.zone, data.startLocal, data.startUtc, data.startOther) : null;
  return time ? `${relative} @ ${time}` : relative;
}
